import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { ConnectedSource, FileNode, AppView } from '@/tools/exmarked/types'

interface AppState {
  // Navigation
  view:             AppView
  selectedSource:   ConnectedSource | null
  selectedFilePath: string | null
  selectedFileId:   string | null   // GitHub: blob SHA | Local: relative path

  // Data
  repos:           ConnectedSource[]
  fileTree:        FileNode[]
  markdownContent: string | null

  // Loading
  isLoadingTree: boolean
  isLoadingFile: boolean

  // Error
  error: string | null

  // ── Actions ──────────────────────────────────────────────────────────────
  setRepos:           (repos: ConnectedSource[]) => void
  selectSource:       (source: ConnectedSource)  => void
  setFileTree:        (tree: FileNode[])         => void
  selectFile:         (path: string, id: string) => void
  setMarkdownContent: (content: string | null)   => void
  setLoadingTree:     (loading: boolean)         => void
  setLoadingFile:     (loading: boolean)         => void
  setError:           (error: string | null)     => void
  backToVault:        () => void
  backToWorkspace:    () => void
  refreshTree:        () => void

  // ── ExPedition ───────────────────────────────────────────────────────────
  expedition:           FileNode[]
  expeditionName:       string
  addToExpedition:      (file: FileNode) => void
  removeFromExpedition: (path: string)   => void
  reorderExpedition:    (fromIdx: number, toIdx: number) => void
  clearExpedition:      () => void
  setExpeditionName:    (name: string)   => void
}

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      view:             'vault',
      selectedSource:   null,
      selectedFilePath: null,
      selectedFileId:   null,
      repos:            [],
      fileTree:         [],
      markdownContent:  null,
      isLoadingTree:    false,
      isLoadingFile:    false,
      error:            null,

      setRepos: (repos) => set({ repos }),

      selectSource: (source) => set({
        selectedSource:   source,
        view:             'workspace',
        selectedFilePath: null,
        selectedFileId:   null,
        markdownContent:  null,
        fileTree:         [],
        isLoadingTree:    true,
        error:            null,
      }),

      setFileTree: (fileTree) => set({ fileTree, isLoadingTree: false }),

      selectFile: (path, id) => {
        // Persist last-opened file per source to localStorage
        const source = get().selectedSource
        if (source?.id) {
          try {
            const map = JSON.parse(
              localStorage.getItem('exmarked:lastOpened') ?? '{}'
            ) as Record<string, { path: string }>
            map[source.id] = { path }
            localStorage.setItem('exmarked:lastOpened', JSON.stringify(map))
          } catch { /* ignore */ }
        }
        set({
          selectedFilePath: path,
          selectedFileId:   id,
          view:             'viewer',
          markdownContent:  null,
          isLoadingFile:    true,
          error:            null,
        })
      },

      setMarkdownContent: (markdownContent) => set({
        markdownContent,
        isLoadingFile: false,
      }),

      setLoadingTree: (isLoadingTree) => set({ isLoadingTree }),
      setLoadingFile: (isLoadingFile) => set({ isLoadingFile }),
      setError:       (error)         => set({ error }),

      backToVault: () => set({
        view:             'vault',
        selectedSource:   null,
        selectedFilePath: null,
        selectedFileId:   null,
        markdownContent:  null,
        fileTree:         [],
        isLoadingTree:    false,
        isLoadingFile:    false,
        error:            null,
      }),

      backToWorkspace: () => set({
        view:             'workspace',
        selectedFilePath: null,
        selectedFileId:   null,
        markdownContent:  null,
        isLoadingFile:    false,
        error:            null,
      }),

      refreshTree: () => set({ isLoadingTree: true }),

      // ── ExPedition ────────────────────────────────────────────────────────
      expedition:     [],
      expeditionName: '',

      addToExpedition: (file) => set((s) => ({
        expedition: s.expedition.some((f) => f.path === file.path)
          ? s.expedition
          : [...s.expedition, file],
      })),

      removeFromExpedition: (path) => set((s) => ({
        expedition: s.expedition.filter((f) => f.path !== path),
      })),

      reorderExpedition: (fromIdx, toIdx) => set((s) => {
        const items = [...s.expedition]
        const [moved] = items.splice(fromIdx, 1)
        items.splice(toIdx, 0, moved)
        return { expedition: items }
      }),

      clearExpedition:   () => set({ expedition: [], expeditionName: '' }),
      setExpeditionName: (expeditionName) => set({ expeditionName }),
    }),
    { name: 'ExMarked' }
  )
)
